import { create } from 'zustand';
import { projects } from '../data/projects';
import type { Project, TransitionRect } from '../types';

type Page = 'loading' | 'home' | 'about' | 'transitioning';

interface AppState {
  // R3F Canvas (for demand mode)
  invalidate: (() => void) | null;

  // Page state
  currentPage: Page;
  loadingProgress: number;

  // Project state
  selectedProject: Project | null;
  selectedIndex: number | null;
  transitionOrigin: TransitionRect | null;

  // Additional state
  isInitialAnimationComplete: boolean;
  hasScrolled: boolean;

  // New state
  nearestProjectIndex: number;

  // New state
  scrollOffset: number;
  
  // New state
  velocity: number;

  // New state
  galleryScrollOffset: number;
  // New state
  isTransitionComplete: boolean;
  isHoveringProject: boolean;
  hasVisitedAbout: boolean;
  hasPlayedIntroVortex: boolean;
  isIndicatorScrolling: boolean;
  mediaNeedsReload: boolean;
  isSoundEnabled: boolean;

  // Actions
  setCurrentPage: (page: Page) => void;
  setLoadingProgress: (progress: number) => void;
  setSelected: (project: Project | null, index: number | null) => void;
  setTransitionOrigin: (rect: TransitionRect | null) => void;
  setInitialAnimationComplete: (value: boolean) => void;
  setHasScrolled: (value: boolean) => void;
  setScrollOffset: (offset: number) => void;
  setVelocity: (velocity: number) => void;
  setGalleryScrollOffset: (offset: number) => void;
  setTransitionComplete: (complete: boolean) => void;
  setHoveringProject: (value: boolean) => void;
  setHasVisitedAbout: (value: boolean) => void;
  setHasPlayedIntroVortex: (value: boolean) => void;
  setIsIndicatorScrolling: (value: boolean) => void;
  setInvalidate: (fn: (() => void) | null) => void;
  setMediaNeedsReload: (value: boolean) => void;
  setSoundEnabled: (value: boolean) => void;
}

export const useAppStore = create<AppState>(set => ({
  // Initial state
  invalidate: null,
  currentPage: 'loading',
  loadingProgress: 0,
  selectedProject: null,
  selectedIndex: null,
  transitionOrigin: null,
  isInitialAnimationComplete: false,
  hasScrolled: false,
  nearestProjectIndex: Math.floor(projects.length / 2),
  scrollOffset: 0,
  velocity: 0,
  galleryScrollOffset: 0,
  isTransitionComplete: false,
  isHoveringProject: false,
  hasVisitedAbout: false,
  hasPlayedIntroVortex: false,
  isIndicatorScrolling: false,
  mediaNeedsReload: false,
  isSoundEnabled: true,

  // Actions
  setCurrentPage: page => set({ currentPage: page }),
  setLoadingProgress: progress => set({ loadingProgress: progress }),
  setSelected: (project, index) =>
    set(state => ({
      selectedProject: project,
      selectedIndex: index,
      isTransitionComplete: false,
      transitionOrigin: project ? state.transitionOrigin : null,
    })),
  setTransitionOrigin: rect => set({ transitionOrigin: rect }),
  setInitialAnimationComplete: value =>
    set({ isInitialAnimationComplete: value }),
  setHasScrolled: value => set({ hasScrolled: value }),
  setScrollOffset: offset => set({ scrollOffset: offset }),
  setVelocity: velocity => set({ velocity }),
  setGalleryScrollOffset: offset => set({ galleryScrollOffset: offset }),
  setTransitionComplete: complete => set({ isTransitionComplete: complete }),
  setHoveringProject: value => set({ isHoveringProject: value }),
  setHasVisitedAbout: value => set({ hasVisitedAbout: value }),
  setHasPlayedIntroVortex: value => set({ hasPlayedIntroVortex: value }),
  setIsIndicatorScrolling: value => set({ isIndicatorScrolling: value }),
  setInvalidate: fn => set({ invalidate: fn }),
  setMediaNeedsReload: value => set({ mediaNeedsReload: value }),
  setSoundEnabled: value => set({ isSoundEnabled: value }),
}));

// Selectors pour un accès plus facile aux données
export const useSelectedProject = () =>
  useAppStore(state => ({
    selectedProject: state.selectedProject,
    selectedIndex: state.selectedIndex,
    setSelected: state.setSelected,
  }));

export const usePageState = () =>
  useAppStore(state => ({
    currentPage: state.currentPage,
    loadingProgress: state.loadingProgress,
    setCurrentPage: state.setCurrentPage,
    setLoadingProgress: state.setLoadingProgress,
  }));
